import { lastMinuteDealsGradient } from '@/app/common/components/Promotions/styled.ts';
import { flashSalesIconSize, lastMinuteDealsIconSize } from '@/app/common/icons/promotionIcons';
import styled, { css } from 'styled-components';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const flashSalesInlineBackground = `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0NSIgaGVpZ2h0PSIzNCIgZmlsbD0ibm9uZSI+PHBhdGggZmlsbD0idXJsKCNhKSIgZD0iTS41IDI4YTYgNiAwIDAgMCA2IDZoMjJsMTYtMzRoLTM4YTYgNiAwIDAgMC02IDZ2MjJaIi8+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJhIiB4MT0iNTYuNSIgeDI9IjI3Ljg0OSIgeTE9Ii0yMC41IiB5Mj0iMTguMDA0IiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHN0b3Agc3RvcC1jb2xvcj0iI0Y1NzMwMCIvPjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iI0ZGQjMwMCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjwvc3ZnPg==`;

/** Gray inset border on the text side (right 3 edges) of an LMD badge. */
const grayBorderAfter = css`
  &:after {
    content: '';
    position: absolute;
    left: 28px;
    right: 0;
    top: 0;
    bottom: 0;
    border: 1px solid ${({ theme }) => theme.kallisto.color.common.black[100]};
    border-radius: 0 6px 6px 0;
    border-left: 0;
  }
`;

const expiringColors = css`
  color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  border: none;
`;

export const FLASH_COUNTER_ICON = flashSalesIconSize(24);
export const FLASH_COUNTER_EVOLUTION_ICON = flashSalesIconSize(16);
export const LMD_COUNTER_ICON = lastMinuteDealsIconSize(20);
export const LMD_COUNTER_EVOLUTION_ICON = lastMinuteDealsIconSize(18);

const flashSalesNormal = (iconPath: string) => css`
  background-color: white;
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimary};
  background-image: url(${iconPath}), url(${flashSalesInlineBackground});
  background-position:
    ${16 - FLASH_COUNTER_ICON / 2}px center,
    -1px top;
  background-size:
    ${FLASH_COUNTER_ICON}px ${FLASH_COUNTER_ICON}px,
    44px 36px;
`;

const flashSalesExpiring = (iconPath: string) => css`
  ${expiringColors};
  background-color: #000000;
  background-image: url(${iconPath}), url(${flashSalesInlineBackground});
  background-position:
    ${16 - FLASH_COUNTER_ICON / 2}px center,
    -1px top;
  background-size:
    ${FLASH_COUNTER_ICON}px ${FLASH_COUNTER_ICON}px,
    44px 36px;
`;

const lmdBackground = (iconPath: string) => css`
  overflow: hidden;
  padding: 3px 8px 3px 42px;
  background-image: url(${iconPath}), ${lastMinuteDealsGradient};
  background-position:
    ${17 - LMD_COUNTER_ICON / 2}px center,
    -1px top;
  background-size:
    ${LMD_COUNTER_ICON}px ${LMD_COUNTER_ICON}px,
    34px 34px;
`;

const lmdNormal = (iconPath: string) => css`
  ${lmdBackground(iconPath)};
  color: #251e2d;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  ${grayBorderAfter};
`;

const lmdExpiring = (iconPath: string) => css`
  ${lmdBackground(iconPath)};
  ${expiringColors};
  background-color: #251e2d;
`;

// ─── Styled Components ────────────────────────────────────────────────────────

export const FlashSalesCounterContainer = styled.div<{
  lastMinuteDeals: boolean;
  lmdIcon: string;
  flashIcon: string;
  isExpiring: boolean;
  right?: boolean;
}>`
  position: absolute;
  top: 16px;
  ${({ right }) =>
    right
      ? css`
          right: 16px;
        `
      : css`
          left: 16px;
        `};
  z-index: 1;
  border-radius: 6px;
  min-width: 110px;
  height: 34px;
  padding: 3px 8px 3px 47px;
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  flex-direction: column;
  background-repeat: no-repeat;

  div {
    color: inherit;
    display: flex;
    align-items: center;
    height: 14px;
    font-weight: 400;
    line-height: 14px;
  }

  ${({ lastMinuteDeals, isExpiring, lmdIcon, flashIcon }) => {
    if (lastMinuteDeals) {
      if (isExpiring) return lmdExpiring(lmdIcon);
      return lmdNormal(lmdIcon);
    }

    if (isExpiring) return flashSalesExpiring(flashIcon);
    return flashSalesNormal(flashIcon);
  }}

  div:last-of-type {
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  }
`;

/** Resets the badge to an inline-flow element. */
const evolutionBaseLayout = css`
  position: relative;
  top: 0;
  right: 0;
  height: 28px;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;

  div {
    height: auto;
  }
`;

const flashSalesEvolution = (iconPath: string, isExpiring: boolean) => css`
  ${evolutionBaseLayout};
  padding: 0 8px 0 36px;
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  background-image:
    url(${iconPath}),
    linear-gradient(
      90deg,
      ${({ theme }) => theme.kallisto.color.common.special.flashSales[400]},
      ${({ theme }) => theme.kallisto.color.common.special.flashSales[400]}
    );
  background-position:
    ${14 - FLASH_COUNTER_EVOLUTION_ICON / 2}px center,
    0 0;
  background-size:
    ${FLASH_COUNTER_EVOLUTION_ICON}px ${FLASH_COUNTER_EVOLUTION_ICON}px,
    28px 100%;
  ${
    isExpiring
      ? css`
          ${expiringColors};
          background-color: #000000;
        `
      : css`
          background-color: white;
          color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimary};
          ${grayBorderAfter};

          &:after {
            border-radius: 0 4px 4px 0;
            border-color: ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
          }
        `
  };
`;

const lmdEvolution = (iconPath: string, isExpiring: boolean) => css`
  ${evolutionBaseLayout};
  overflow: hidden;
  padding: 0 8px 0 36px;
  font-size: 14px;
  background-image: url(${iconPath}), ${lastMinuteDealsGradient};
  background-position:
    ${14 - LMD_COUNTER_EVOLUTION_ICON / 2}px center,
    0 0;
  background-size:
    ${LMD_COUNTER_EVOLUTION_ICON}px ${LMD_COUNTER_EVOLUTION_ICON}px,
    28px 100%;
  ${
    isExpiring
      ? css`
          ${expiringColors};
          background-color: #251e2d;
        `
      : css`
          color: #251e2d;
          background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
          ${grayBorderAfter};

          &:after {
            border-radius: 0 4px 4px 0;
            border-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
          }
        `
  };
`;

export const FlashSalesCounterEvolutionContainer = styled.div<{
  lastMinuteDeals: boolean;
  lmdIcon: string;
  flashIcon: string;
  isExpiring: boolean;
}>`
  border-radius: 4px;
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  background-repeat: no-repeat;

  div {
    color: inherit;
    display: flex;
    align-items: center;
    font-weight: 400;
    line-height: 20px;
  }

  ${({ lastMinuteDeals, isExpiring, lmdIcon, flashIcon }) => {
    if (lastMinuteDeals) return lmdEvolution(lmdIcon, isExpiring);
    return flashSalesEvolution(flashIcon, isExpiring);
  }}

  div:last-of-type {
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  }
`;

export const FlashSalesCounterContainerSlim = styled.div<{
  isExpiring?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  color: ${(p) => p.theme.kallisto.color.text.static.textOnLight};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};

  height: 34px;
  padding: 0 8px;

  div {
    color: inherit;
    display: flex;
    align-items: center;
    height: 14px;
    font-weight: 400;
    line-height: 14px;
  }

  div:last-of-type {
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  }
`;
